import Flex from "@/components/atoms/Flex";
import IconButton from "@/components/atoms/IconButton";
import { checkIfUserHasRoles } from "@/helpers/UserHelpers";
import { useDeleteCompanyMutation } from "@/services/companies";
import { selectCurrentUser } from "@/services/slices/authSlice";
import {
  faArrowLeft,
  faCalendar,
  faEdit,
  faEllipsisVertical,
  faGraduationCap,
  faMailForward,
  faUserDoctor,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { FetchBaseQueryError } from "@reduxjs/toolkit/dist/query";
import { ElementRef, FC, useRef } from "react";
import { Card, Dropdown } from "react-bootstrap";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { ToastContentProps, toast } from "react-toastify";
import AreYouSurToContinueModal from "../../modals/AreYouSurToContinueModal";
import SendCompanyBrochuresModal from "../../modals/company/SendCompanyBrochuresModal";
import SendCompanyConventionsModal from "../../modals/company/SendCompanyConventionsModal";
import StoreCompanyFileModal from "../../modals/company/StoreCompanyFileModal";
import StoreCompanyMeetingModal from "../../modals/company/StoreCompanyMeetingModal";
import StoreCompanyUserModal from "../../modals/company/StoreCompanyUserModal";
import StoreCompanyCourseModal from "../../modals/company/courses/StoreCompanyCourseModal";
import StorePersonModal from "../../modals/person/StorePersonModal";

export interface CompanyHeaderProps {
  id: string;
  isEdit?: boolean;
  archived?: boolean;
}

const CompanyHeader: FC<CompanyHeaderProps> = ({ id, isEdit, archived }) => {
  const user = useSelector(selectCurrentUser);
  const DeleteModalRef =
    useRef<ElementRef<typeof AreYouSurToContinueModal>>(null);
  const StoreCompanyFileModalRef =
    useRef<ElementRef<typeof StoreCompanyFileModal>>(null);
  const StoreCompanyCourseModalRef =
    useRef<ElementRef<typeof StoreCompanyCourseModal>>(null);
  const StorePersonModalRef = useRef<ElementRef<typeof StorePersonModal>>(null);
  const StoreCompanyMeetingModalRef =
    useRef<ElementRef<typeof StoreCompanyMeetingModal>>(null);
  const StoreCompanyUserModalRef =
    useRef<ElementRef<typeof StoreCompanyUserModal>>(null);
  const SendCompanyBrochuresModalRef =
    useRef<ElementRef<typeof SendCompanyBrochuresModal>>(null);
  const SendCompanyConventionsModalRef =
    useRef<ElementRef<typeof SendCompanyConventionsModal>>(null);
  const [deleteCompanyAsync] = useDeleteCompanyMutation();

  const navigation = useNavigate();

  const onDelete = async () => {
    toast.promise(deleteCompanyAsync(id).unwrap(), {
      pending: "Archive de l'entreprise en cours ...",
      success: {
        render() {
          DeleteModalRef.current?.showOrHiddenModal();
          return (
            <p style={{ marginBottom: 0, textAlign: "center" }}>
              L'entreprise à bien été archivée 🤩
            </p>
          );
        },
      },
      error: {
        render({ data }: ToastContentProps<FetchBaseQueryError>) {
          if (data?.status === 422) {
            return "Les champs que vous avez remplis semblent être incorrects.";
          }

          return "Une erreur est survenue";
        },
      },
    });
  };

  return (
    <Card className="mb-3 overflow-visible position-relative z-index-2">
      <AreYouSurToContinueModal
        ref={DeleteModalRef}
        onContinue={onDelete}
        title="Souhaitez-vous vraiment archivé l'entreprise qui a pour id : {{id}} ?"
      />
      <StoreCompanyMeetingModal
        ref={StoreCompanyMeetingModalRef}
        companyId={id}
      />
      <StoreCompanyFileModal ref={StoreCompanyFileModalRef} companyId={id} />
      <StoreCompanyUserModal ref={StoreCompanyUserModalRef} companyId={id} />
      <StorePersonModal ref={StorePersonModalRef} companyId={id} />
      <StoreCompanyCourseModal ref={StoreCompanyCourseModalRef} id={id} />
      <SendCompanyBrochuresModal
        ref={SendCompanyBrochuresModalRef}
        companyId={id}
      />
      <SendCompanyConventionsModal
        ref={SendCompanyConventionsModalRef}
        companyId={id}
      />
      <Card.Header className="d-flex flex-between-center">
        <IconButton
          onClick={() => navigation(-1)}
          variant="nolina-default"
          size="sm"
          icon={faArrowLeft}
        />
        <Flex>
          {!isEdit && (
            <>
              <IconButton
                variant="nolina-default"
                href={`/companies/${id}/edit`}
                size="sm"
                icon={faEdit}
                transform="shrink-2"
                iconAlign="middle"
                className="me-3 me-xl-2"
              >
                <span className="ms-1">Modifier</span>
              </IconButton>
              {checkIfUserHasRoles(user, ["ADMIN", "AE", "CSM"]) && (
                <>
                  <IconButton
                    variant="nolina-default"
                    size="sm"
                    icon={faGraduationCap}
                    transform="shrink-2"
                    iconAlign="middle"
                    className="me-2"
                    overlayTitle="Ajouter une formation"
                    onClick={() =>
                      StoreCompanyCourseModalRef.current?.showOrHiddenModal()
                    }
                  >
                    <span className="d-none d-xl-inline-block ms-1">
                      Ajouter une formation
                    </span>
                  </IconButton>
                  <IconButton
                    variant="nolina-default"
                    size="sm"
                    icon={faUserDoctor}
                    transform="shrink-2"
                    iconAlign="middle"
                    className="me-2"
                    overlayTitle="Ajouter un employé"
                    onClick={() =>
                      StorePersonModalRef.current?.showOrHiddenModal()
                    }
                  >
                    <span className="d-none d-xl-inline-block ms-1">
                      Ajouter un salarié
                    </span>
                  </IconButton>
                </>
              )}
            </>
          )}
          <IconButton
            variant="nolina-default"
            size="sm"
            icon={faCalendar}
            transform="shrink-2"
            iconAlign="middle"
            className="me-2"
            overlayTitle="Créer un ticket"
            onClick={() =>
              StoreCompanyMeetingModalRef.current?.showOrHiddenModal()
            }
          >
            <span className="d-none d-xl-inline-block ms-1">
              Crée un rendez-vous
            </span>
          </IconButton>

          <div
            className="bg-300 me-3 d-xl-none"
            style={{ width: "1px", height: "29px" }}
          ></div>
          {!isEdit && (
            <Dropdown align="end" className="btn-reveal-trigger d-inline-block">
              <Dropdown.Toggle split variant="nolina-default" size="sm">
                <FontAwesomeIcon icon={faEllipsisVertical} className="fs--2" />
              </Dropdown.Toggle>

              <Dropdown.Menu className="border py-0">
                <div className="py-2">
                  {checkIfUserHasRoles(user, ["ADMIN", "AE", "CSM"]) && (
                    <Dropdown.Item
                      onClick={() =>
                        StoreCompanyFileModalRef.current?.showOrHiddenModal()
                      }
                    >
                      Ajouter un fichier
                    </Dropdown.Item>
                  )}
                  {checkIfUserHasRoles(user, ["ADMIN"]) && (
                    <>
                      <Dropdown.Item
                        onClick={() =>
                          StoreCompanyUserModalRef.current?.showOrHiddenModal()
                        }
                      >
                        Ajouter un utilisateur
                      </Dropdown.Item>
                      <Dropdown.Divider />
                      <Dropdown.Item
                        className={archived ? "text-success" : "text-danger"}
                        onClick={() =>
                          DeleteModalRef.current?.showOrHiddenModal(id)
                        }
                      >
                        {archived ? "Désarchiver" : "Archiver"}
                      </Dropdown.Item>
                    </>
                  )}
                  {checkIfUserHasRoles(user, [
                    "ADMIN",
                    "GENERALMANAGER",
                    "AE",
                    "SDR",
                  ]) && (
                    <Dropdown.Item
                      className="text-info"
                      onClick={() =>
                        SendCompanyBrochuresModalRef.current?.showOrHiddenModal()
                      }
                    >
                      <FontAwesomeIcon
                        icon={faMailForward}
                        className="me-2"
                      />
                      Envoyer des plaquettes
                    </Dropdown.Item>
                  )}
                  {checkIfUserHasRoles(user, ["ADMIN", "AE", "CSM"]) && (
                    <Dropdown.Item
                      className="text-info"
                      onClick={() =>
                        SendCompanyConventionsModalRef.current?.showOrHiddenModal()
                      }
                    >
                      <FontAwesomeIcon
                        icon={faMailForward}
                        className="me-2"
                      />
                      Envoyer des conventions
                    </Dropdown.Item>
                  )}
                </div>
              </Dropdown.Menu>
            </Dropdown>
          )}
        </Flex>
      </Card.Header>
    </Card>
  );
};

export default CompanyHeader;
